/**
 * T-Shaped Unit Composer
 *
 * Combines T-shaped floor and wall segments for a single T-hangar unit.
 * Handles alternating facing (south/north) and Z-shaped partition walls.
 *
 * Wall segment layout for a south-facing unit (wing at +Z, tail at -Z):
 *
 *   front (unit_width, at +Z)  ← door wall
 *   back  (tail_width, at -Z)
 *   right_wing  (wing_depth, right side of wing bay, along Z)
 *   right_step  (step_width, horizontal step, along X)
 *   right_tail  (wing_depth, right side of tail bay, along Z)
 *   left_wing   (wing_depth, left side of wing bay, along Z)
 *   left_step   (step_width, horizontal step, along X)
 *   left_tail   (wing_depth, left side of tail bay, along Z)
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedUnit, DoorSpec } from '@/lib/templates/types'
import type { THangarEdgeSide } from '@/lib/templates/standard-t-hangar/edges'
import { shouldRenderTHangarEdge } from '@/lib/templates/standard-t-hangar/edges'
// Facing is now determined from unit.unit_rotation, not index parity
import { renderTShapedFloor, type TShapeDimensions } from '../primitives/TShapedFloorRenderer'
import {
  createWallMaterial,
  createPartitionMaterial,
  createDoorMaterial,
  applyShadowSettings,
} from '../utils/materials'

export interface TUnitRenderOptions {
  wallColor?: string
  floorColor?: string
  wallThickness?: number // feet
  enableShadows?: boolean
  buildingName?: string
  /** Skip per-unit floor (orchestrator renders a unified building floor instead) */
  skipFloor?: boolean
}

// ============================================================================
// WALL SEGMENT POSITIONING
// ============================================================================

/**
 * Calculate position and rotation for each wall segment of a T-shaped unit.
 *
 * Returns position relative to the unit center (center of bounding box:
 * unitWidth × 2*wingDepth). All values in feet.
 */
function getWallSegmentGeometry(
  edgeId: THangarEdgeSide,
  dims: TShapeDimensions,
  thickness: number,
  facing: 'south' | 'north'
): {
  position: [number, number, number] // [x, y, z] in feet, y = vertical
  rotation: number // Y-axis rotation in radians
  length: number // feet
} {
  const { unitWidth, wingDepth, tailWidth } = dims
  const tailDepth = dims.tailDepth ?? wingDepth
  const stepWidth = (unitWidth - tailWidth) / 2
  const totalDepth = wingDepth + tailDepth
  const halfWidth = unitWidth / 2
  const halfDepth = totalDepth / 2
  const halfTail = tailWidth / 2
  const halfThk = thickness / 2

  // Flip factor: south-facing has wing at +Z, north-facing has wing at -Z
  const f = facing === 'south' ? 1 : -1

  switch (edgeId) {
    case 'front':
      // Full-width door wall — at the wing end
      return {
        position: [0, 0, f * (halfDepth + halfThk)],
        rotation: 0,
        length: unitWidth,
      }
    case 'back':
      // Tail-width back wall — at the tail end
      return {
        position: [0, 0, f * -(halfDepth + halfThk)],
        rotation: 0,
        length: tailWidth,
      }
    case 'right_wing':
      // Right side of wing bay — vertical wall along Z
      return {
        position: [halfWidth + halfThk, 0, f * (halfDepth - wingDepth / 2)],
        rotation: Math.PI / 2,
        length: wingDepth,
      }
    case 'right_step':
      // Right horizontal step — along X between wing and tail
      return {
        position: [halfWidth - stepWidth / 2, 0, f * (halfDepth - wingDepth)],
        rotation: 0,
        length: stepWidth,
      }
    case 'right_tail':
      // Right side of tail bay — vertical wall along Z
      return {
        position: [halfTail + halfThk, 0, f * -(halfDepth - tailDepth / 2)],
        rotation: Math.PI / 2,
        length: tailDepth,
      }
    case 'left_wing':
      // Left side of wing bay — vertical wall along Z
      return {
        position: [-(halfWidth + halfThk), 0, f * (halfDepth - wingDepth / 2)],
        rotation: Math.PI / 2,
        length: wingDepth,
      }
    case 'left_step':
      // Left horizontal step — along X between wing and tail
      return {
        position: [-(halfWidth - stepWidth / 2), 0, f * (halfDepth - wingDepth)],
        rotation: 0,
        length: stepWidth,
      }
    case 'left_tail':
      // Left side of tail bay — vertical wall along Z
      return {
        position: [-(halfTail + halfThk), 0, f * -(halfDepth - tailDepth / 2)],
        rotation: Math.PI / 2,
        length: tailDepth,
      }
  }
}

// ============================================================================
// WALL RENDERING
// ============================================================================

/**
 * Create a solid wall box
 */
function createSolidWall(
  length: number,
  height: number,
  thickness: number,
  material: THREE.Material,
  enableShadows: boolean
): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(
    length * FEET_TO_METERS,
    height * FEET_TO_METERS,
    thickness * FEET_TO_METERS
  )
  const mesh = new THREE.Mesh(geometry, material)
  applyShadowSettings(mesh, enableShadows, enableShadows)
  return mesh
}

/**
 * Create a wall with door openings (used for front wall)
 */
function createWallWithDoor(
  wallLength: number,
  wallHeight: number,
  thickness: number,
  openings: DoorSpec[],
  wallMaterial: THREE.Material,
  enableShadows: boolean
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'wallWithDoor'

  const sortedOpenings = [...openings].sort((a, b) => a.offset_ft - b.offset_ft)
  let currentPos = 0

  for (const opening of sortedOpenings) {
    const openingStart = opening.offset_ft
    const openingEnd = opening.offset_ft + opening.width_ft
    const doorCenterX = opening.offset_ft + opening.width_ft / 2

    // Wall segment before door
    if (currentPos < openingStart) {
      const segLen = openingStart - currentPos
      const seg = createSolidWall(segLen, wallHeight, thickness, wallMaterial, enableShadows)
      seg.position.x = (currentPos + segLen / 2 - wallLength / 2) * FEET_TO_METERS
      seg.name = 'wallSegment'
      group.add(seg)
    }

    // Lintel above door
    if (opening.height_ft < wallHeight) {
      const lintelH = wallHeight - opening.height_ft
      const lintel = new THREE.Mesh(
        new THREE.BoxGeometry(
          opening.width_ft * FEET_TO_METERS,
          lintelH * FEET_TO_METERS,
          thickness * FEET_TO_METERS
        ),
        wallMaterial
      )
      lintel.position.set(
        (doorCenterX - wallLength / 2) * FEET_TO_METERS,
        ((wallHeight + opening.height_ft) / 2 - wallHeight / 2) * FEET_TO_METERS,
        0
      )
      lintel.name = 'lintel'
      applyShadowSettings(lintel, enableShadows, enableShadows)
      group.add(lintel)
    }

    // Door visualization (semi-transparent)
    const doorMat = createDoorMaterial(opening.type)
    const doorMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(
        opening.width_ft * FEET_TO_METERS,
        opening.height_ft * FEET_TO_METERS
      ),
      doorMat
    )
    doorMesh.position.set(
      (doorCenterX - wallLength / 2) * FEET_TO_METERS,
      (opening.height_ft / 2 - wallHeight / 2) * FEET_TO_METERS,
      thickness / 2 * FEET_TO_METERS + 0.01
    )
    doorMesh.name = `door-${opening.type}`
    group.add(doorMesh)

    currentPos = openingEnd
  }

  // Wall segment after last door
  if (currentPos < wallLength) {
    const segLen = wallLength - currentPos
    const seg = createSolidWall(segLen, wallHeight, thickness, wallMaterial, enableShadows)
    seg.position.x = (currentPos + segLen / 2 - wallLength / 2) * FEET_TO_METERS
    seg.name = 'wallSegment'
    group.add(seg)
  }

  return group
}

// ============================================================================
// PARTITION CORNER FILLERS
// ============================================================================

/**
 * Create small filler boxes at the 90° corners of Z-shaped partition walls.
 *
 * Each Z-partition has 3 segments (wing, step, tail) that meet at 90° angles.
 * The box geometries don't share vertices at the corners, leaving small gaps.
 * Two filler boxes per partition side seal these gaps.
 */
function createPartitionCornerFillers(
  dims: TShapeDimensions,
  facing: 'south' | 'north',
  side: 'left' | 'right',
  height: number,
  thickness: number,
  material: THREE.Material,
  enableShadows: boolean
): THREE.Mesh[] {
  const { unitWidth, wingDepth, tailWidth } = dims
  const tailDepth = dims.tailDepth ?? wingDepth
  const halfWidth = unitWidth / 2
  const halfTail = tailWidth / 2
  const halfThk = thickness / 2
  const totalDepth = wingDepth + tailDepth
  const halfDepth = totalDepth / 2

  const f = facing === 'south' ? 1 : -1
  const s = side === 'right' ? 1 : -1

  const fillers: THREE.Mesh[] = []
  const boxGeo = new THREE.BoxGeometry(
    thickness * FEET_TO_METERS,
    height * FEET_TO_METERS,
    thickness * FEET_TO_METERS
  )

  // Corner 1: wing-step junction
  const c1 = new THREE.Mesh(boxGeo, material)
  c1.position.set(
    s * (halfWidth + halfThk) * FEET_TO_METERS,
    (height / 2) * FEET_TO_METERS,
    f * (halfDepth - wingDepth) * FEET_TO_METERS
  )
  c1.name = `corner-${side}-wing-step`
  applyShadowSettings(c1, enableShadows, enableShadows)
  fillers.push(c1)

  // Corner 2: step-tail junction
  const c2 = new THREE.Mesh(boxGeo, material)
  c2.position.set(
    s * (halfTail + halfThk) * FEET_TO_METERS,
    (height / 2) * FEET_TO_METERS,
    f * (halfDepth - wingDepth) * FEET_TO_METERS
  )
  c2.name = `corner-${side}-step-tail`
  applyShadowSettings(c2, enableShadows, enableShadows)
  fillers.push(c2)

  return fillers
}

// ============================================================================
// UNIT RENDERING
// ============================================================================

/**
 * Render a single T-hangar unit
 */
export function renderTUnit(
  unit: GeneratedUnit,
  _unitIndex: number,
  options: TUnitRenderOptions = {}
): THREE.Group {
  const {
    wallColor,
    floorColor,
    wallThickness = 0.5,
    enableShadows = true,
    skipFloor = false,
  } = options

  const tEdges = unit.tHangarEdges
  if (!tEdges || tEdges.length === 0) {
    console.warn(`renderTUnit: Unit "${unit.code}" has no tHangarEdges`)
    return new THREE.Group()
  }

  const group = new THREE.Group()
  group.name = `unit-${unit.code}`
  group.userData = {
    unitCode: unit.code,
    unitType: unit.type,
  }

  // Extract dimensions from type_config or unit itself
  // The unit stores interior_width = unit_width, interior_depth = wing_depth + tail_depth
  const unitWidth = unit.interior_width
  // Simpler: get tail_width from the back edge length
  const backEdge = tEdges.find(e => e.id === 'back')
  const actualTailWidth = backEdge?.length_ft ?? unitWidth * 0.5

  // Derive wing_depth: the wing edges have the wing depth as their length
  const wingEdge = tEdges.find(e => e.id === 'right_wing' || e.id === 'left_wing')
  const wingDepth = wingEdge?.length_ft ?? unit.interior_depth / 2
  // tail_depth = interior_depth - wing_depth (may differ from wing_depth)
  const tailDepth = unit.interior_depth - wingDepth

  // Use unit_rotation to determine facing (supports both alternating and same-direction layouts)
  // rotation ≈ 0 → south-facing, rotation ≈ PI → north-facing
  const southFacing = (unit.unit_rotation ?? 0) < Math.PI / 2
  const facing = southFacing ? 'south' : 'north'

  const dims: TShapeDimensions = {
    unitWidth,
    wingDepth,
    tailWidth: actualTailWidth,
    tailDepth,
  }

  // Render T-shaped floor (skipped when orchestrator provides a unified building floor)
  if (!skipFloor) {
    const floor = renderTShapedFloor(dims, facing, {
      color: floorColor,
      enableShadows,
    })
    group.add(floor)
  }

  // Render wall segments (only those we should render)
  const edgesToRender = tEdges.filter(shouldRenderTHangarEdge)

  for (const edge of edgesToRender) {
    const wallMat = edge.type === 'partition'
      ? createPartitionMaterial({ color: wallColor })
      : createWallMaterial({ color: wallColor })

    const geo = getWallSegmentGeometry(edge.id, dims, wallThickness, facing)
    const height = edge.height_ft

    let wallObj: THREE.Object3D

    if (edge.openings && edge.openings.length > 0) {
      // Wall with door openings (front wall)
      wallObj = createWallWithDoor(
        geo.length,
        height,
        wallThickness,
        edge.openings,
        wallMat,
        enableShadows
      )
    } else {
      wallObj = createSolidWall(geo.length, height, wallThickness, wallMat, enableShadows)
    }

    // Position the wall segment
    wallObj.position.set(
      geo.position[0] * FEET_TO_METERS,
      (height / 2) * FEET_TO_METERS, // Center vertically
      geo.position[2] * FEET_TO_METERS
    )

    // Apply rotation for walls along Z axis
    if (geo.rotation !== 0) {
      wallObj.rotation.y = geo.rotation
    }

    wallObj.name = `wall-${edge.id}`
    group.add(wallObj)
  }

  // Add corner filler boxes at Z-partition junctions to seal 90° gaps
  const partitionMat = createPartitionMaterial({ color: wallColor })
  const wallHeight = tEdges[0]?.height_ft ?? 14

  // Right-side partitions are always rendered by this unit (lower-index owns)
  const hasRightPartition = edgesToRender.some(e => e.id === 'right_wing' && e.type === 'partition')
  if (hasRightPartition) {
    const fillers = createPartitionCornerFillers(dims, facing, 'right', wallHeight, wallThickness, partitionMat, enableShadows)
    fillers.forEach(f => group.add(f))
  }

  // Right-side exterior edges (last unit) also need corner fillers
  const hasRightExterior = edgesToRender.some(e => e.id === 'right_wing' && e.type === 'exterior')
  if (hasRightExterior) {
    const extMat = createWallMaterial({ color: wallColor })
    const fillers = createPartitionCornerFillers(dims, facing, 'right', wallHeight, wallThickness, extMat, enableShadows)
    fillers.forEach(f => group.add(f))
  }

  // Left-side exterior edges (first unit) also need corner fillers
  const hasLeftExterior = edgesToRender.some(e => e.id === 'left_wing' && e.type === 'exterior')
  if (hasLeftExterior) {
    const extMat = createWallMaterial({ color: wallColor })
    const fillers = createPartitionCornerFillers(dims, facing, 'left', wallHeight, wallThickness, extMat, enableShadows)
    fillers.forEach(f => group.add(f))
  }

  // Position unit within building (offset_x is left edge)
  const unitCenterX = unit.offset_x + unitWidth / 2
  group.position.set(
    unitCenterX * FEET_TO_METERS,
    0,
    -unit.offset_y * FEET_TO_METERS // DB +Y = north, scene -Z = north
  )

  return group
}
